import { getCurrentInstance } from 'vue';
import useMobile from '@/common/hooks/useMobile';
import { isAndroid } from 'mobile-device-detect';

/**
 * Common provisioning hook for controller setup over BLE or WiFi(AP).
 *
 * Philosophy:
 * - Return Promises only (no UI decisions here).
 * - Do minimal, safe cleanup (remove event listeners).
 * - Let pages own: loading text UX, retry loops, fallback strategy (BLE -> WiFi).
 */
export default function useControllerProvisioning() {
  const { emitter, mobile, logger } =
    getCurrentInstance().appContext.config.globalProperties;

  const {
    // WiFi(AP)
    connectWifi: connectWifiFromMobile,
    disConnectWifi,
    // BLE
    getBleAdapterState,
    scanBleDevices,
    connectBleDevice,
    provisionBle,
    disconnectBle,
    getDeviceInfo,
  } = useMobile();

  const normalizeControllerInfoFromSerial = (serialNo, version = '') => {
    const arr = `${serialNo || ''}`.split('_');
    return {
      _id: serialNo || '',
      model: arr[0] || '',
      version: version?.toString?.() || `${version || ''}`,
    };
  };

  /**
   * WiFi(AP) connect with progress messages.
   * Mirrors pages that listen to onConnectResultMsg + onConnectResult.
   */
  const connectWifiAp = (params = {}) => {
    return new Promise((resolve, reject) => {
      const onProgress =
        typeof params?.onProgress === 'function' ? params.onProgress : null;

      const cleanup = () => {
        emitter.off('onConnectResult');
        emitter.off('onConnectResultMsg');
      };

      emitter.on('onConnectResultMsg', (payload) => {
        if (payload?.msg) {
          if (onProgress) onProgress(payload.msg);
          else emitter.emit('appendLoadingText', payload.msg);
        }
      });

      emitter.on('onConnectResult', (payload) => {
        // Pages typically call showLoading before initiating WiFi(AP) connection.
        // Ensure we always stop the loading indicator when result arrives.
        emitter.emit('hideLoading');
        cleanup();

        try {
          if (!payload?.result) {
            // 1001: phone wifi is off (native convention)
            if (payload?.resultCode === 1001) {
              reject({
                resultCode: 1001,
                message: 'wifi가 켜져있지 않습니다.\nwifi를 켜고 다시 시도해주세요.',
              });
              return;
            }
            reject({ message: '연결에 실패하였습니다.' });
            return;
          }

          const decoded = JSON.parse(decodeURIComponent(window.atob(payload.data)));
          if (decoded?.message) {
            reject({ message: decoded.message });
            return;
          }

          const device = decoded?.device;
          if (!device?.serial_no) {
            reject({ message: '연결에 실패하였습니다.' });
            return;
          }

          resolve({
            decoded,
            controllerInfo: normalizeControllerInfoFromSerial(
              device.serial_no,
              device.version,
            ),
          });
        } catch (e) {
          reject({ message: '연결에 실패하였습니다.', error: e });
        }
      });

      // Trigger connect. If caller passed params, use them; else fall back to useMobile default ap_wifi.
      if (params?.ssid) {
        mobile.connectWifi(
          JSON.stringify({
            ssid: params.ssid,
            password: params.password,
            bssid: params.bssid || undefined,
            callback: 'onConnectResult',
          }),
        );
      } else {
        // uses useMobile's internal default AP credentials
        connectWifiFromMobile(params).then(
          (info) => resolve({ decoded: null, controllerInfo: normalizeControllerInfoFromSerial(info.serial_no, info.version) }),
          (err) => reject({ message: `${err}` }),
        );
      }
    });
  };

  const disconnectWifiApSafe = (force = false, apWifi = undefined) => {
    try {
      if (apWifi?.ssid) {
        mobile.disConnectWifi(JSON.stringify(force ? { force: 'true' } : apWifi));
      } else {
        disConnectWifi(force);
      }
    } catch (_) {}
  };

  /**
   * Provision controller over WiFi(AP) using mobile.settingDevice().
   * This keeps resultCode semantics used across pages.
   */
  const provisionOverWifiAp = ({
    settingParams,
    apWifi,
    timeoutMs = 30000,
    disconnectAfter = true,
    onProgress,
  }) => {
    return new Promise((resolve, reject) => {
      if (!settingParams?.ssid || `${settingParams.ssid}`.length === 0) {
        reject({ resultCode: 'VALIDATION', message: 'wifi 정보를 입력해주세요.' });
        return;
      }

      let timeoutId = undefined;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = undefined;
        }
        emitter.off('onSettingResult');
        emitter.off('onSettingResultMsg');
      };

      timeoutId = setTimeout(() => {
        cleanup();
        reject({ resultCode: 'TIMEOUT', message: '응답을 받을 수 없습니다.' });
      }, timeoutMs);

      emitter.on('onSettingResultMsg', (payload) => {
        if (payload?.msg) {
          if (typeof onProgress === 'function') onProgress(payload.msg);
          else emitter.emit('appendLoadingText', payload.msg);
        }
      });

      emitter.on('onSettingResult', (payload) => {
        cleanup();

        // result 값 확인 (resultCode보다 우선)
        if (payload?.result === false) {
          reject({
            resultCode: 1005,
            message: '조명제어기 설정에 실패했습니다.',
          });
          return;
        }

        const rc = payload?.resultCode;
        if (rc === 1002) {
          reject({
            resultCode: 1002,
            message:
              'ssid에 해당하는 wifi를 찾을 수 없습니다.\nssid를 정확하게 입력해주세요.',
          });
          return;
        }
        if (rc === 1003) {
          reject({
            resultCode: 1003,
            message: isAndroid
              ? '입력된 wifi 정보가 잘못되었습니다.\nwifi 비밀번호를 확인해주세요.'
              : '입력된 wifi 정보가 잘못되었습니다.\nwifi 정보를 확인해주세요.',
          });
          return;
        }
        if (rc === 1004) {
          reject({ resultCode: 1004, message: '조명제어기가 연결되어 있지 않습니다.' });
          return;
        }
        if (rc === 1005) {
          reject({ resultCode: 1005, message: '조명제어기 설정에 실패했습니다.' });
          return;
        }

        if (disconnectAfter) {
          disconnectWifiApSafe(false, apWifi);
        }

        resolve(payload);
      });

      mobile.settingDevice(
        JSON.stringify({
          ...settingParams,
          callback: 'onSettingResult',
        }),
      );
    });
  };

  // BLE helpers (thin wrappers around useMobile)
  const checkBleAdapter = async () => {
    const state = await getBleAdapterState();
    if (!state?.supported || !state?.isOn) {
      throw new Error(
        `BLE를 사용할 수 없습니다.\n\n지원 여부: ${state?.supported ? '지원' : '미지원'}\n상태: ${
          state?.isOn ? '켜짐' : '꺼짐'
        }`,
      );
    }
    return state;
  };

  /**
   * BLE 스캔을 재시도 로직과 함께 수행합니다.
   * @param {Object} params - 스캔 파라미터
   * @param {number} params.maxRetries - 최대 재시도 횟수 (기본값: 2)
   * @param {number} params.timeout - 스캔 타임아웃 (초, 기본값: 5)
   * @param {string} params.filterPrefix - 장치 이름 필터 접두사 (기본값: 'inshow_m')
   * @returns {Promise<Array>} 발견된 BLE 장치 배열
   */
  const scanBleWithRetry = async ({
    maxRetries = 0,
    timeout = 5,
    filterPrefix = 'inshow_m',
  } = {}) => {
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const devices = await scanBleDevices({ timeout, filterPrefix });
        
        if (Array.isArray(devices) && devices.length > 0) {
          logger.log(`BLE scan succeeded on attempt ${attempt + 1}, found ${devices.length} device(s)`);
          return devices;
        }
        
        // 장치를 찾지 못한 경우
        if (attempt < maxRetries) {
          logger.log(`BLE scan found no devices on attempt ${attempt + 1}, retrying...`);
          await new Promise((r) => setTimeout(r, 1000)); // 재시도 전 대기
          continue;
        }
        
        // 마지막 시도에서도 장치를 찾지 못한 경우
        throw new Error(
          'BLE 장치를 찾을 수 없습니다.\n\n조명제어기가 셋팅 모드로 되어 있는지 확인해주세요.\n(장치 이름이 "inshow_m"으로 시작해야 합니다)',
        );
      } catch (e) {
        lastError = e;
        logger.log(`BLE scan failed on attempt ${attempt + 1}:`, e);
        
        if (attempt < maxRetries) {
          // 재시도 가능한 에러인 경우에만 재시도
          const errorMsg = e?.message || e?.toString() || '';
          if (
            errorMsg.includes('BLE를 사용할 수 없습니다') ||
            errorMsg.includes('BLE not available') ||
            errorMsg.includes('BLE adapter')
          ) {
            // BLE 어댑터 문제는 재시도해도 소용없으므로 즉시 throw
            throw e;
          }
          
          await new Promise((r) => setTimeout(r, 1000)); // 재시도 전 대기
          continue;
        }
        
        // 마지막 시도에서도 실패한 경우
        throw lastError;
      }
    }
    
    // 이론적으로 도달하지 않지만 타입 안전성을 위해
    throw lastError || new Error('BLE 스캔에 실패했습니다.');
  };

  const scanAndMatchBleDevice = async ({
    targetSerial,
    filterPrefix = 'inshow_m',
    timeout = 5,
  }) => {
    const devices = await scanBleDevices({ timeout, filterPrefix });
    if (!Array.isArray(devices) || devices.length === 0) {
      throw new Error(
        'BLE 장치를 찾을 수 없습니다.\n\n조명제어기가 셋팅 모드로 되어 있는지 확인해주세요.\n(장치 이름이 "inshow_m"으로 시작해야 합니다)',
      );
    }
    await new Promise((r) => setTimeout(r, 1000));
    const matched = devices.find((d) => `${d.name || ''}`.includes(targetSerial));
    if (!matched?.deviceId) throw new Error('BLE 장치를 찾지 못했습니다.');
    return matched;
  };

  const connectBleBySerial = async ({ targetSerial, filterPrefix, timeout }) => {
    await checkBleAdapter();
    const matched = await scanAndMatchBleDevice({ targetSerial, filterPrefix, timeout });
    await connectBleDevice(matched.deviceId, null, matched.name);
    
    // 장치 이름에서 직접 정보 추출 (getDeviceInfo 호출 불필요)
    let serialNo = matched.name || '';
    if (serialNo.startsWith('InShow_')) {
      serialNo = serialNo.substring('InShow_'.length);
    }
    
    if (!serialNo) {
      await disconnectBleSafe();
      throw new Error('BLE 장치 이름에서 정보를 추출할 수 없습니다.');
    }
    
    return {
      matchedDevice: matched,
      deviceInfo: {
        serial_no: serialNo,
        version: 0,
      },
      controllerInfo: normalizeControllerInfoFromSerial(
        serialNo,
        0,
      ),
    };
  };

  const disconnectBleSafe = async () => {
    try {
      await disconnectBle();
    } catch (_) {}
  };

  const provisionOverBle = async (params) => {
    return provisionBle(params);
  };

  return {
    // WiFi(AP)
    connectWifiAp,
    disconnectWifiApSafe,
    provisionOverWifiAp,

    // BLE
    checkBleAdapter,
    scanBleWithRetry,
    scanAndMatchBleDevice,
    connectBleBySerial,
    disconnectBleSafe,
    provisionOverBle,

    // utils
    normalizeControllerInfoFromSerial,
  };
}

